import type { MemberOut, RoleOut, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Segmented, TableSkeletonRows } from "@sagot/ui";
import { useState } from "react";
import { useFormatters } from "../features/workspace/shared/format";
import { usePermissions } from "../session/permissions";
import { MemberRow, type MemberRowActions } from "./members-table";
import { PendingInviteRows } from "./pending-invites";

type Tab = "all" | "pending";
const COLUMNS = 6;

interface MembersCardProps extends MemberRowActions {
  members: { data: MemberOut[] | undefined; isPending: boolean; isError: boolean; refetch: () => unknown };
  roles: readonly RoleOut[] | undefined;
  teams: readonly TeamOut[];
  query: string;
}

/** Members table card: All / Pending, inline role select, 2FA status, last active, row menu. */
export function MembersCard({ members, roles, teams, query, onEditRoles, onRemove }: MembersCardProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const format = useFormatters();
  const [tab, setTab] = useState<Tab>("all");
  const canManageRoles = permissions.can("roles.manage") && !permissions.readOnly;
  const canManageTeam = permissions.can("team.manage") && !permissions.readOnly;
  const canInvite = permissions.can("team.invite") && !permissions.readOnly;
  const needle = query.trim().toLowerCase();
  const visible = (members.data ?? []).filter((m) => m.status !== "removed" && (!needle || `${m.name} ${m.email}`.toLowerCase().includes(needle)));
  const relative = (iso: string | null) => (iso ? format.relative(iso) : t("team.never"));
  const body = () => {
    if (tab === "pending") return <PendingInviteRows columns={COLUMNS} members={members.data ?? []} teams={teams} canInvite={canInvite} format={{ date: format.shortDate, relative: format.relative }} />;
    if (members.isPending || !roles) return <TableSkeletonRows rows={5} columns={COLUMNS} />;
    if (visible.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMNS}>
            <EmptyState icon="search" headingLevel={3} title={t("team.emptyTitle")} description={t("team.emptyBody")} />
          </td>
        </tr>
      );
    }
    return visible.map((member) => (
      <MemberRow
        key={member.id}
        member={member}
        roles={roles}
        teams={teams}
        canManageRoles={canManageRoles}
        canManageTeam={canManageTeam}
        relative={relative}
        onEditRoles={onEditRoles}
        onRemove={onRemove}
      />
    ));
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="members-title">
      <div className="card-header">
        <h2 id="members-title" className="card-title">
          {t("team.members")} <span className="ml-1 font-medium text-fg-subtle">{members.data ? visible.length : ""}</span>
        </h2>
        <Segmented aria-label={t("team.members")} value={tab} onValueChange={setTab} options={[{ value: "all", label: t("team.all") }, { value: "pending", label: t("team.pending") }]} />
      </div>
      {members.isError ? (
        <ErrorState title={t("team.loadError")} onRetry={() => void members.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t("team.colMember")}</th>
                <th className="w-48">{t("team.colRole")}</th>
                <th>{t("team.colTeams")}</th>
                <th>{t("team.col2fa")}</th>
                <th>{t("team.colLastActive")}</th>
                <th className="w-12">
                  <span className="sr-only">{t("team.colActions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>{body()}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
